import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader, Chip } from "@/components/ui-bits";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  LogOut, Save, User as UserIcon, Phone, Cake, Building2, Briefcase,
  ChevronRight, Sun, Moon, Bell, Lock, Mail, Globe,
} from "lucide-react";
import SuccessCheckmark from "@/components/SuccessCheckmark";
import AvatarUpload from "@/components/AvatarUpload";
import SecuritySection from "@/pages/SecuritySection";

// Fase 5 / 5.1 — 3-way theme picker (light / dark / system) shared with AppShell.
// Persistence: localStorage for pre-render/FOUC-safe first paint, backend PATCH
// (`users.theme`) for cross-device roaming. localStorage is only a cache — the
// authoritative store is the user record.
import { readTheme, saveTheme, applyTheme, effectiveTheme, watchSystemTheme } from "@/lib/theme";

function ThemeSelector({ t }) {
  const { user, setUser } = useAuth();
  // Prefer the server-side theme when we have it — falls back to localStorage.
  const [pref, setPref] = useState(() => (user?.theme && ["light", "dark", "system"].includes(user.theme)) ? user.theme : readTheme());
  useEffect(() => {
    applyTheme(pref);
    saveTheme(pref);
    // Persist across devices. Fire-and-forget: a network hiccup falls back to
    // the localStorage cache, which the next bootstrap will reconcile.
    if (user?._id && user.theme !== pref) {
      api.patch(`/users/${user._id}`, { theme: pref }).then((res) => {
        if (res.data) setUser((u) => (u ? { ...u, theme: pref } : u));
      }).catch(() => {});
    }
    if (pref !== "system") return;
    return watchSystemTheme(() => applyTheme("system"));
  }, [pref]);   // eslint-disable-line react-hooks/exhaustive-deps
  const OPTIONS = [
    { v: "light",  label: t("theme_light"),  Icon: Sun },
    { v: "dark",   label: t("theme_dark"),   Icon: Moon },
    { v: "system", label: t("theme_system"), Icon: Globe },
  ];
  return (
    <div
      className="flex items-center gap-1 bg-[hsl(var(--muted))] rounded-2xl p-1 h-[46px] w-full"
      data-testid="profile-theme"
      data-theme-pref={pref}
      data-theme-effective={effectiveTheme(pref)}
    >
      {OPTIONS.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => setPref(o.v)}
          data-testid={`profile-theme-${o.v}`}
          className={`flex-1 h-full rounded-xl flex items-center justify-center gap-2 text-[13px] font-semibold transition-all ${pref === o.v ? "bg-[hsl(var(--surface))] text-[hsl(var(--text-strong))] shadow-[var(--shadow-sm)]" : "text-[hsl(var(--text-muted))]"}`}
        >
          <o.Icon size={14} strokeWidth={2} />
          {o.label}
        </button>
      ))}
    </div>
  );
}

// One clean field row — collapses lock-indicator into a small icon inside the input.
function Field({ label, icon: Icon, disabled, children }) {
  return (
    <label className="flex flex-col gap-1.5 min-w-0">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-[hsl(var(--text-muted))] flex items-center gap-1.5">
        {Icon && <Icon size={11} strokeWidth={2.2} />}
        {label}
        {disabled && <Lock size={10} strokeWidth={2.4} className="text-[hsl(var(--text-muted))] opacity-70" />}
      </span>
      {children}
    </label>
  );
}

export default function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const { t, setLang } = useI18n();
  const navigate = useNavigate();
  const isManager = ["owner", "manager", "teamlead"].includes(user?.role);
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    language: user?.language || "nl",
    date_of_birth: user?.date_of_birth || "",
    department: user?.department || "",
    job_title: user?.job_title || "",
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false); // controls SuccessCheckmark overlay

  const save = async () => {
    try {
      setBusy(true);
      const payload = { ...form };
      if (!isManager) { delete payload.name; delete payload.date_of_birth; }
      await api.patch(`/users/${user._id}`, payload);
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 1400);
    } catch {
      toast.error(t("error"));
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success(t("logout"));
  };

  const inputCls = "input-field !h-11";
  const inputDisabledCls = "input-field !h-11 !bg-[hsl(var(--muted))] !text-[hsl(var(--text-muted))] cursor-not-allowed";

  return (
    <div data-testid="page-profile" className="max-w-5xl mx-auto">
      <PageHeader
        eyebrow={t("profile")}
        title={t("profile")}
        subtitle={t("profile_subtitle") || "Beheer je gegevens en voorkeuren."}
      />

      {/* --- Identity card — clean, generous whitespace --- */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="card p-7 mb-4"
        data-testid="profile-identity"
      >
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 text-center sm:text-left">
          <AvatarUpload
            user={user}
            size={96}
            onChange={() => refreshUser?.()}
          />
          <div className="flex-1 min-w-0 flex flex-col items-center sm:items-start pt-1">
            <div className="font-display text-[26px] font-bold tracking-tight text-[hsl(var(--text-strong))] leading-tight break-words">
              {user?.name}
            </div>
            <div className="text-[13px] text-[hsl(var(--text-muted))] mt-1 flex items-center gap-1.5">
              <Mail size={12} strokeWidth={2} className="opacity-60" />
              <span className="truncate">{user?.email}</span>
            </div>
            <div className="flex items-center gap-1.5 mt-3.5 flex-wrap justify-center sm:justify-start">
              <Chip variant="primary" data-testid="profile-role-chip">{t(`role_${user?.role}`)}</Chip>
              {user?.job_title && <Chip variant="neutral">{user.job_title}</Chip>}
              {user?.department && <Chip variant="neutral">{user.department}</Chip>}
            </div>
          </div>
        </div>
      </motion.div>

      {/* --- Read-only hint for employees --- */}
      {!isManager && (
        <div
          className="rounded-2xl border hairline p-3.5 mb-4 flex items-start gap-2.5"
          style={{ background: "hsl(var(--muted) / 0.5)" }}
          data-testid="profile-readonly-hint"
        >
          <div className="w-7 h-7 rounded-lg bg-[hsl(var(--surface))] grid place-items-center shrink-0">
            <Lock size={12} className="text-[hsl(var(--primary))]" strokeWidth={2.2} />
          </div>
          <div className="text-[12px] leading-relaxed text-[hsl(var(--text-muted))]">
            {t("profile_readonly_hint") || "Je persoonsgegevens staan hier alleen ter informatie. Kloppen ze niet? Vraag je manager of teamleider om ze aan te passen."}
          </div>
        </div>
      )}

      {/* --- Data card — 2-column grid on desktop, single column on mobile --- */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="card p-6 mb-4"
        data-testid="profile-data-card"
      >
        <div className="section-header !mt-0 !mb-5" style={{ paddingBottom: 0 }}>{t("profile_section_data") || "Gegevens"}</div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("name")} icon={UserIcon} disabled={!isManager}>
            <input
              data-testid="profile-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={isManager ? inputCls : inputDisabledCls}
              disabled={!isManager}
            />
          </Field>

          <Field label={t("phone")} icon={Phone} disabled={!isManager}>
            <input
              data-testid="profile-phone"
              value={form.phone || ""}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+31 6 ..."
              className={isManager ? inputCls : inputDisabledCls}
              disabled={!isManager}
            />
          </Field>

          <Field label={t("job_function")} icon={Briefcase} disabled={!isManager}>
            <input
              data-testid="profile-jobtitle"
              value={form.job_title || ""}
              onChange={(e) => setForm({ ...form, job_title: e.target.value })}
              placeholder={t("job_placeholder")}
              className={isManager ? inputCls : inputDisabledCls}
              disabled={!isManager}
            />
          </Field>

          <Field label={t("department_col")} icon={Building2} disabled={!isManager}>
            <input
              data-testid="profile-department"
              value={form.department || ""}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
              placeholder={t("dept_placeholder")}
              className={isManager ? inputCls : inputDisabledCls}
              disabled={!isManager}
            />
          </Field>

          <div className="sm:col-span-2">
            <Field label={t("birthday_col")} icon={Cake} disabled={!isManager}>
              <input
                data-testid="profile-dob"
                type="date"
                value={form.date_of_birth || ""}
                onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                className={isManager ? inputCls + " sm:!w-1/2" : inputDisabledCls + " sm:!w-1/2"}
                disabled={!isManager}
              />
              {isManager && (
                <span className="text-[11px] text-[hsl(var(--text-muted))] mt-1 inline-block">{t("birthday_privacy_hint")}</span>
              )}
            </Field>
          </div>
        </div>

        {isManager && (
          <button
            onClick={save} disabled={busy}
            className="btn btn-primary mt-6"
            data-testid="profile-save"
          >
            <Save size={16} strokeWidth={2} /> {busy ? "..." : t("save")}
          </button>
        )}
      </motion.div>

      {/* --- Voorkeuren card (separate) --- */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 }}
        className="card p-6 mb-4"
        data-testid="profile-prefs-card"
      >
        <div className="section-header !mt-0 !mb-5" style={{ paddingBottom: 0 }}>{t("profile_section_prefs") || "Voorkeuren"}</div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("language")} icon={Globe}>
            <select
              data-testid="profile-language"
              value={form.language}
              onChange={(e) => {
                const next = e.target.value;
                setForm({ ...form, language: next });
                // Instantly re-render the entire app in the new language AND
                // persist to backend via I18nContext (which PATCHes /users/me/language).
                setLang(next);
              }}
              className={inputCls}
            >
              <option value="nl">Nederlands</option>
              <option value="en">English</option>
            </select>
          </Field>

          <Field label={t("theme")} icon={Sun}>
            <ThemeSelector t={t} />
          </Field>
        </div>
      </motion.div>

      {/* --- Preferences link (notifications) --- */}
      <motion.button
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.09 }}
        onClick={() => navigate("/preferences")}
        className="card card-lift p-5 mb-4 flex items-center gap-4 w-full text-left"
        data-testid="profile-preferences-link"
      >
        <div className="w-11 h-11 rounded-2xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
          <Bell size={17} strokeWidth={1.9} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))]">{t("preferences_label")}</div>
          <div className="text-[12.5px] text-[hsl(var(--text-muted))] mt-0.5 leading-relaxed">
            {t("preferences_desc")}
          </div>
        </div>
        <ChevronRight size={18} className="text-[hsl(var(--text-muted))] shrink-0" />
      </motion.button>

      {/* --- Security card (Phase 9B.2.1 — canonical personal security location) ---
          SecuritySection is self-scoped (every role manages their own MFA and email
          verification). It was previously only reachable via /settings (owner-only);
          promoting it here makes MFA available for owner/manager/teamlead/employee
          without duplicating the UI. Backend endpoints (/auth/mfa/*) remain
          require_current_user — no RBAC changes. */}
      <div data-testid="profile-security-anchor" id="security">
        <SecuritySection />
      </div>

      {/* --- Session / logout — clearly the "end" of the page --- */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.11 }}
        className="card p-5 flex items-center justify-between gap-4"
        data-testid="profile-session-card"
      >
        <div className="min-w-0">
          <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))]">{t("session")}</div>
          <div className="text-[12.5px] text-[hsl(var(--text-muted))] mt-0.5 truncate">
            {t("logout_device")}
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="btn btn-danger shrink-0"
          data-testid="profile-logout"
        >
          <LogOut size={16} strokeWidth={2} /> {t("logout")}
        </button>
      </motion.div>

      {/* Floating Apple-like success checkmark on save */}
      <AnimatePresence>
        {saved && (
          <motion.div
            key="profile-saved-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[60] grid place-items-center bg-black/25 backdrop-blur-[2px] pointer-events-none"
            data-testid="profile-saved-overlay"
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 22 }}
              className="card px-8 py-7"
            >
              <SuccessCheckmark label={t("ok_profile_saved")} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
